import { useQuery } from "@tanstack/react-query";
import { listBudgets } from "../Budgets/api";

const API_URL = process.env.REACT_APP_API_URL;

export function normalizeSpendDetails(details = []) {
  return details.map((d) => ({
    time: d.groupings?.[0]?.value,
    grouping: d.groupings?.[1]?.value,
    measure: d.measures?.[0]?.value,
  }));
}

// shadowSeriesFromSpend collapses per-(day,grouping) spend points into a per-day
// API-equivalent (shadow) total line — only when the backend returned shadowMeasure
// (withShadow). Returns [] when no point carried shadow, so the chart hides the line.
export function shadowSeriesFromSpend(series = []) {
  const byDay = {};
  let any = false;
  for (const r of series) {
    if (r.shadowMeasure == null) continue;
    any = true;
    byDay[r.time] = (byDay[r.time] || 0) + Number(r.shadowMeasure || 0);
  }
  if (!any) return [];
  return Object.entries(byDay)
    .sort((a, b) => String(a[0]).localeCompare(String(b[0])))
    .map(([time, measure]) => ({ time, measure }));
}

// forecastSeries — the Impact tab's projection fan is sourced directly from
// /api/v1/investigations/preview's decomposition.impact.forecast.series
// (backend_go/internal/investigation/types.go ForecastImpact/ForecastSeriesPoint),
// which is already scope-consistent (same filters/grouping as the rest of the
// preview) and already marks past days via `actual` vs forward days via
// `forecast`/`lower`/`upper` — no heuristic needed, unlike /get-forecast.
export function forecastSeries(decomposition) {
  return decomposition?.impact?.forecast?.series || [];
}

// ownershipDriversFor — maps a perspective's ownership lens (decomposition.ownership,
// backend_go internal/investigation OwnershipLens: `{ dimensionCode, dimensionId,
// rows: [{ ownerNodeId, label, deltaCost }], coveragePct, confidenceLabel }`) into the
// `{ dimension, key, label, deltaCost, sharePct }` driver-row shape MoversDriversPanel /
// DriverDecompositionTable already render. Ownership rows have no price/volume split,
// so `priceVolume` is omitted (both consumers already handle its absence). Pure/no I/O.
export function ownershipDriversFor(decomposition, dimensionCode) {
  const lens = (decomposition?.ownership || []).find((l) => l.dimensionCode === dimensionCode);
  if (!lens) return [];
  const rows = lens.rows || [];
  const total = rows.reduce((sum, r) => sum + Math.abs(r.deltaCost || 0), 0);
  return rows
    .map((row) => ({
      dimension: dimensionCode,
      key: String(row.ownerNodeId),
      label: row.label || `Owner #${row.ownerNodeId}`,
      deltaCost: row.deltaCost,
      sharePct: total > 0 ? (100 * Math.abs(row.deltaCost || 0)) / total : 0,
    }))
    .sort((a, b) => Math.abs(b.deltaCost || 0) - Math.abs(a.deltaCost || 0));
}

// driversAtLevel — rolls perspective drivers (from ownershipDriversFor, which
// returns the WHOLE ownership lens across every depth) UP to the node set the
// stacked chart shows at the current drill level (Analyzer.jsx computes levelIds
// from usePerspectiveTree the same way the backend scopes usePerspectiveSeries).
// Each descendant driver is re-attributed to its display-level ancestor and
// summed there — the same subtree roll-up the chart (perspective-series) and the
// hierarchy page perform — so a parent whose cost lives entirely on descendants
// (0 direct delta, so no lens row of its own) still shows its rolled-up mover
// instead of vanishing. Without this the panel FILTERED to level-node ids and
// dropped every descendant row, so it listed only the lone top-level node that
// happened to carry direct cost (e.g. "Unallocated / Shared Services").
//
// `treeNodes` (usePerspectiveTree: {id, label, parentId}) supplies the parent
// pointers + level-node labels. Pure/no I/O. `levelIds == null` means the tree
// hasn't loaded yet, so drivers pass through unchanged rather than render empty
// while waiting on the tree fetch. A loaded tree whose current level has zero
// nodes (e.g. a drilled leaf with no children) is a real, non-null empty Set —
// that yields [] (no drivers), matching the empty-chart "No sub-nodes" state.
// A driver whose node has no ancestor in levelIds (a different subtree, or a
// stale node absent from the tree) is dropped rather than mis-attributed.
export function driversAtLevel(drivers, levelIds, treeNodes = []) {
  if (levelIds == null) return drivers;

  const parentById = new Map();
  const labelById = new Map();
  for (const n of treeNodes) {
    parentById.set(Number(n.id), n.parentId == null ? null : Number(n.parentId));
    labelById.set(Number(n.id), n.label);
  }

  // ancestorAtLevel walks up the parent chain to the first node in levelIds
  // (memoized per origin node); null when no ancestor is in the level set. A
  // visited guard bounds the walk against a malformed (cyclic) parent map.
  const memo = new Map();
  const ancestorAtLevel = (id) => {
    if (memo.has(id)) return memo.get(id);
    const seen = new Set();
    let cur = id;
    while (cur != null && !seen.has(cur)) {
      if (levelIds.has(cur)) { memo.set(id, cur); return cur; }
      seen.add(cur);
      cur = parentById.has(cur) ? parentById.get(cur) : null;
    }
    memo.set(id, null);
    return null;
  };

  const byAncestor = new Map();
  for (const d of drivers) {
    const target = ancestorAtLevel(Number(d.key));
    if (target == null) continue;
    const row = byAncestor.get(target) || {
      dimension: d.dimension,
      key: String(target),
      label: labelById.get(target) || d.label || `Owner #${target}`,
      deltaCost: 0,
    };
    row.deltaCost += d.deltaCost || 0;
    byAncestor.set(target, row);
  }

  const rolled = Array.from(byAncestor.values());
  const total = rolled.reduce((sum, r) => sum + Math.abs(r.deltaCost || 0), 0);
  return rolled
    .map((r) => ({ ...r, sharePct: total > 0 ? (100 * Math.abs(r.deltaCost || 0)) / total : 0 }))
    .sort((a, b) => Math.abs(b.deltaCost || 0) - Math.abs(a.deltaCost || 0));
}

function authHeaders(token) {
  const h = new Headers();
  h.append("Content-Type", "application/json");
  h.append("Authorization", "Bearer " + token);
  return h;
}

// `enabled` defaults to true for back-compat; Analyzer.jsx passes
// `!isPerspective` so a perspective dimension (sourced from
// usePerspectiveSeries instead) doesn't also fire a redundant spend-series
// request — its groupingField is "" so the request would be the ungrouped
// total, not the perspective breakdown.
//
// AI2: sources the flat/total spend chart from POST
// /api/v1/analyzer/spend-series (allocation_cost_base_daily, AI-inclusive —
// AI1's backend contract) instead of /get-cloud-cost (int_f_costs_agg,
// ~GCP-only), so the analyzer's spend reconciles with drivers/forecast/
// perspectives on the same cost base. Rows are already shaped
// {time, grouping, measure} (same as usePerspectiveSeries) — pass through,
// no normalizeSpendDetails mapping needed.
export function useAnalyzerSpend(scope, token, enabled = true) {
  const body = scope.buildSpendSeriesRequest();
  return useQuery(["analyzerSpend", body], async () => {
    const res = await fetch(API_URL + "/api/v1/analyzer/spend-series", { method: "POST", headers: authHeaders(token), body: JSON.stringify(body) });
    if (!res.ok) throw new Error("spend-series failed");
    const json = await res.json();
    const series = json.spend?.series || [];
    return { details: series, grouped_totals: [], shadow: shadowSeriesFromSpend(series), noConfirmedInvoices: !!json.spend?.noConfirmedInvoices };
  }, { enabled: !!token && enabled, staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false });
}

// useAnalyzerAiSavings — GET /api/v1/ai-cost/summary?from=&to= for the analyzer's
// window: AI actual (re-based) vs shadow (API-equivalent) vs effective discount.
// Envelope is `{ ai_cost: { actual, shadow, effective_discount_pct } }`.
export function useAnalyzerAiSavings(from, to, token) {
  return useQuery(["analyzerAiSavings", from, to], async () => {
    const url = API_URL + "/api/v1/ai-cost/summary?from=" + encodeURIComponent(from) + "&to=" + encodeURIComponent(to);
    const res = await fetch(url, { headers: authHeaders(token) });
    if (!res.ok) throw new Error("ai-cost summary failed");
    const json = await res.json();
    const s = json.ai_cost || {};
    return { actual: Number(s.actual) || 0, shadow: Number(s.shadow) || 0, discountPct: Number(s.effective_discount_pct) || 0 };
  }, { enabled: !!token && !!from && !!to, staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false });
}

// useAnalyzerAttribution — POST /api/v1/analyzer/attribution, the "which
// dimension moved the number most" table's data source (feeds a later task's
// UI). Envelope is `{ attribution: { baselineCost, comparisonCost,
// totalDelta, dimensions: [{ dimension, dimensionLabel, topMover: { key,
// label, deltaCost, sharePct }, runners, concentration }] } }` (writeJSON
// wraps under "attribution" — see reference_writejson-envelope-wrapping).
// `enabled` defaults to true, mirroring useAnalyzerSpend, so a future caller
// can gate it (e.g. only fetch when the attribution panel is visible).
export function useAnalyzerAttribution(scope, token, enabled = true) {
  const body = scope.buildAttributionRequest();
  return useQuery(["analyzerAttribution", body], async () => {
    const res = await fetch(API_URL + "/api/v1/analyzer/attribution", { method: "POST", headers: authHeaders(token), body: JSON.stringify(body) });
    if (!res.ok) throw new Error("attribution failed");
    const json = await res.json();
    return {
      dimensions: json.attribution?.dimensions || [],
      totalDelta: json.attribution?.totalDelta || 0,
      baselineCost: json.attribution?.baselineCost || 0,
      comparisonCost: json.attribution?.comparisonCost || 0,
    };
  }, { enabled: !!token && enabled, staleTime: 2 * 60 * 1000, refetchOnWindowFocus: false });
}

export function useAnalyzerDrivers(scope, token) {
  const seed = scope.buildPreviewSeed();
  return useQuery(["analyzerDrivers", seed], async () => {
    const res = await fetch(API_URL + "/api/v1/investigations/preview", { method: "POST", headers: authHeaders(token), body: JSON.stringify(seed) });
    if (!res.ok) throw new Error("preview failed");
    const json = await res.json();
    const d = json.decomposition || {};
    return {
      drivers: d.drivers || [],
      deltaCost: d.deltaCost || 0,
      forecast: d.impact?.forecast || { available: false, series: [] },
      decomposition: d,
    };
  }, { enabled: !!token, staleTime: 2 * 60 * 1000, refetchOnWindowFocus: false });
}

// usePerspectiveTree — GET /api/v1/analyzer/perspective-tree, the owner-node
// hierarchy for one allocation perspective (backend_go
// cmd/api/analyzer_perspective_handlers.go GetAnalyzerPerspectiveTree).
// Envelope is `{ tree: { nodes: [{ id, label, parentId, level }] } }`
// (writeJSON wraps under "tree" — see reference_writejson-envelope-wrapping).
export function usePerspectiveTree(dimensionCode, token) {
  return useQuery(["perspectiveTree", dimensionCode], async () => {
    const res = await fetch(API_URL + "/api/v1/analyzer/perspective-tree?dimensionCode=" + encodeURIComponent(dimensionCode), {
      headers: authHeaders(token),
    });
    if (!res.ok) throw new Error("perspective-tree failed");
    const json = await res.json();
    return json.tree?.nodes || [];
  }, { enabled: !!token && !!dimensionCode, staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false });
}

// useBudgets — active budgets for BudgetSelect (BUD1), sourced from the
// existing budgets/api.js `listBudgets`. Envelope is `{ budgets: [...] }`
// (confirmed via BudgetsListPage.jsx: `data?.budgets || []`; not writeJSON-
// wrapped under a different key). Mapped down to the minimal `{ id, name }`
// shape the dropdown needs.
export function useBudgets(token) {
  const { data } = useQuery(["analyzerBudgets"], () => listBudgets({ status: "active" }), {
    enabled: !!token, staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false,
  });
  return (data?.budgets || []).map((b) => ({ id: b.id, name: b.name }));
}

// useBudgetSeries — GET /api/v1/financial-control/budget-series?budget_id=<id>&period=<YYYY-MM>
// (BUD2), the cumulative burn-up series behind BudgetBurnupChart. CONFIRMED shape
// (backend_go cmd/api/financial_control_series.go financialControlBudgetSeries):
// `resp := map[string]interface{}{"series": series, "cap": capEUR}` written via
// `app.writeJSON(w, http.StatusOK, resp, "")` — the empty wrap key means writeJSON
// marshals resp AS-IS (utilities.go: `if wrap == "" { out, err = json.Marshal(data) }`),
// so the body is the raw `{series, cap}` object — NOT wrapped under a named key like
// `.CloudCost`/`.perspective`. `budgetId` is a string; "0" is the whole-project Total
// pseudo-budget (a valid selection, distinct from "" = no budget selected) and is passed
// through unchanged as the `budget_id` query param.
export function useBudgetSeries(budgetId, period, token) {
  return useQuery(["budgetSeries", budgetId, period], async () => {
    const url = API_URL + "/api/v1/financial-control/budget-series?budget_id="
      + encodeURIComponent(budgetId) + "&period=" + encodeURIComponent(period);
    const res = await fetch(url, { headers: authHeaders(token) });
    if (!res.ok) throw new Error("budget-series failed");
    const json = await res.json();
    return { series: json.series || [], cap: json.cap ?? null };
  }, {
    enabled: !!token && budgetId !== "" && budgetId != null && !!period,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

// usePerspectiveSeries — POST /api/v1/analyzer/perspective-series, the
// per-day per-owner-node spend rows for one allocation perspective at the
// current drill level (backend_go PostAnalyzerPerspectiveSeries). Rows are
// already shaped `{ time, grouping, measure }` — the same shape
// normalizeSpendDetails produces for /get-cloud-cost — so AnalyzerChart
// consumes them unchanged. Envelope is `{ perspective: { series: [...] } }`.
export function usePerspectiveSeries({ dimensionCode, parentOwnerNodeId = null, startDate, endDate }, token) {
  const body = { dimensionCode, parentOwnerNodeId, from: startDate, to: endDate };
  return useQuery(["perspectiveSeries", body], async () => {
    const res = await fetch(API_URL + "/api/v1/analyzer/perspective-series", { method: "POST", headers: authHeaders(token), body: JSON.stringify(body) });
    if (!res.ok) throw new Error("perspective-series failed");
    const json = await res.json();
    return json.perspective?.series || [];
  }, { enabled: !!token && !!dimensionCode, staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false });
}

// useForecastBreakdown — POST /api/v1/analyzer/forecast-series, the forward series per
// segment of the current breakdown, plus the authoritative total and its band. Disabled
// when the breakdown has no forward source (Total, and any dimension the forecast tables
// don't carry — see `enabled`), so the chart simply keeps today's behaviour. Envelope is
// `{ forecast: { groups: [{day, group, cost}], total: [{day, cost, lower, upper}] } }`
// (writeJSON wraps under "forecast" — see reference_writejson-envelope-wrapping); those
// field names are a wire contract shared with forecastBreakdown.js, so pass them through
// unrenamed.
export function useForecastBreakdown({ from, to, breakdown, filters = [], ownerScopes = [] }, token, enabled = true) {
  // filters/ownerScopes arrive PRE-SPLIT (useAnalyzerScope's splitFilters), same as
  // every other analyzer request. Sending neither left a filtered actual stack under
  // a completely unfiltered projection. The backend applies the chips the forward
  // dimension table carries and refuses the ones it cannot (source_key, perspective
  // scopes, anything on an ownership breakdown) — a refusal throws here and the chart
  // falls back to actuals only, which is the honest outcome.
  const body = { from, to, breakdown, filters: filters || [], ownerScopes: ownerScopes || [] };
  return useQuery(["analyzerForecastBreakdown", body], async () => {
    const res = await fetch(API_URL + "/api/v1/analyzer/forecast-series", { method: "POST", headers: authHeaders(token), body: JSON.stringify(body) });
    if (!res.ok) throw new Error("forecast-series failed");
    const json = await res.json();
    return json.forecast || { groups: [], total: [] };
  }, { enabled: !!token && !!breakdown && enabled, staleTime: 2 * 60 * 1000, refetchOnWindowFocus: false });
}

// useDimensionValues — the distinct existing values of a cost-base column, powering
// the Add-filter value dropdown. Reuses POST /api/v1/analyzer/spend-series grouped by
// the field: the distinct `grouping` values ARE that field's values over the window
// (no new endpoint). `enabled` gates the fetch to when the value picker is open.
export function useDimensionValues(field, from, to, token, enabled = true) {
  return useQuery(["analyzerDimensionValues", field, from, to], async () => {
    const res = await fetch(API_URL + "/api/v1/analyzer/spend-series", {
      method: "POST",
      headers: authHeaders(token),
      body: JSON.stringify({ groupingField: field, filters: [], from, to }),
    });
    if (!res.ok) throw new Error("dimension-values failed");
    const json = await res.json();
    const set = new Set();
    for (const r of (json.spend?.series || [])) {
      const g = r.grouping;
      if (g != null && g !== "") set.add(String(g));
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, { enabled: !!token && !!field && enabled, staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false });
}

// useComparisonSpend — per-day TOTAL spend over a comparison window (prior period or
// prior year), respecting the current filters, for the period-comparison "ghost" line.
// groupingField:"" so each row is that day's total. Rows: [{ time, measure }].
//
// Bug fix: filters/ownerScopes must arrive PRE-SPLIT (via useAnalyzerScope's
// splitFilters), same as buildSpendSeriesRequest/buildAttributionRequest/
// buildPreviewSeed — a perspective filter (category = dimensionCode, e.g.
// "organisation") is not a cost-base field and the backend 400s if it rides
// along on `filters` with no ownerScopes. `ownerScopes` also correctly scopes
// the ghost line to the same owner subtree as the rest of the view.
// Defaults to [] for back-compat with callers/tests that don't pass it.
export function useComparisonSpend(from, to, filters, token, enabled = true, ownerScopes = []) {
  const body = { groupingField: "", filters: filters || [], ownerScopes: ownerScopes || [], from, to };
  return useQuery(["analyzerComparisonSpend", body], async () => {
    const res = await fetch(API_URL + "/api/v1/analyzer/spend-series", {
      method: "POST",
      headers: authHeaders(token),
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error("comparison-spend failed");
    const json = await res.json();
    return (json.spend?.series || []).map((r) => ({ time: r.time, measure: Number(r.measure) || 0 }));
  }, { enabled: !!token && !!from && !!to && enabled, staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false });
}
